import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";
import { createReducer, on, Action, ActionReducer, UPDATE, INIT } from "@ngrx/store";

import { InventoryActions } from "./inventory.actions";
import { Inventory, InventoryResponse, Requirement, AvailableRoomCount } from "@estatex/types";

export const INVENTORY_FEATURE_KEY = "inventory";

export interface InventoryState extends EntityState<Inventory> {
    selectedId?: string | number; // which Inventory record has been selected
    loaded: boolean; // has the Inventory list been loaded
    error?: string | null; // last known error (if any)
    requirement: Requirement; // Storing the booking requirement
    inventory: InventoryResponse;
    availableRooms: AvailableRoomCount; // Storing the available room types
}

export interface InventoryPartialState {
    readonly [INVENTORY_FEATURE_KEY]: InventoryState;
}

export const inventoryAdapter: EntityAdapter<Inventory> = createEntityAdapter<Inventory>();

export const initialInventoryState: InventoryState = inventoryAdapter.getInitialState({
    // set initial required properties
    loaded: false,
    requirement: null,
    inventory: null,
    availableRooms: null,
});

const reducer = createReducer(
    initialInventoryState,
    on(InventoryActions.initInventory, (state) => ({ ...state, loaded: false, error: null })),
    on(InventoryActions.loadInventorySuccess, (state, { inventory }) => inventoryAdapter.setAll(inventory, { ...state, loaded: true })),
    on(InventoryActions.loadInventoryFailure, (state, { error }) => ({ ...state, error })),

    on(InventoryActions.saveRequirement, (state, { requirement }) => ({ ...state, requirement })),
    on(InventoryActions.saveInventory, (state, { inventory }) => ({ ...state, inventory })),
    on(InventoryActions.setAvailableRooms, (state, { availableRooms }) => ({ ...state, availableRooms }))
);

export function inventoryReducer(state: InventoryState | undefined, action: Action) {
    return reducer(state, action);
}

export const inventoryHydrationMetaReducer = (reducer: ActionReducer<InventoryState>): ActionReducer<InventoryState> => {
    return (state, action) => {
        if (action.type === INIT || action.type === UPDATE) {
            const storageValue = localStorage.getItem("inventory");
            if (storageValue) {
                try {
                    return JSON.parse(storageValue);
                } catch {
                    localStorage.removeItem("inventory");
                }
            }
        }
        const nextState = reducer(state, action);
        localStorage.setItem("inventory", JSON.stringify(nextState));
        return nextState;
    };
};
